import { useEffect, useRef } from "react";
import { prefersReducedMotion } from "../hud/motion";

// The dashed line from the selected camera's node on the ring to the camera
// panel (mockup #tether). Measured from the DOM, eased when the selection
// moves. Hidden by CSS where the panel does not sit beside the ring.

type Pts = [number, number, number, number]; // node x, y, panel x, y

function measure(): Pts | null {
  const node = document.querySelector("[data-node-sel] .mark");
  const feed = document.getElementById("cam-feed");
  const scene = document.querySelector(".scene");
  if (!node || !feed || !scene) return null;
  const a = node.getBoundingClientRect();
  const b = feed.getBoundingClientRect();
  const s = scene.getBoundingClientRect();
  if (!a.width || !b.width) return null;
  return [a.left + a.width / 2 - s.left, a.top + a.height / 2 - s.top, b.right - s.left, b.top + 12 - s.top];
}

function pathFor([x1, y1, x2, y2]: Pts): string {
  return `M${x1.toFixed(1)} ${y1.toFixed(1)} L${(x1 - 30).toFixed(1)} ${y2.toFixed(1)} L${(x2 + 8).toFixed(1)} ${y2.toFixed(1)}`;
}

export function Tether({ selectedId, layoutKey }: { selectedId: string | null; layoutKey: string }) {
  const pathRef = useRef<SVGPathElement>(null);
  const last = useRef<Pts | null>(null);
  const anim = useRef<number | null>(null);

  useEffect(() => {
    const draw = (animate: boolean) => {
      const el = pathRef.current;
      if (!el) return;
      const to = measure();
      if (anim.current !== null) cancelAnimationFrame(anim.current);
      anim.current = null;
      if (!to) {
        el.setAttribute("d", "");
        last.current = null;
        return;
      }
      const from = last.current;
      if (!animate || !from || prefersReducedMotion()) {
        el.setAttribute("d", pathFor(to));
        last.current = to;
        return;
      }
      const t0 = performance.now();
      const step = (t: number) => {
        const k = Math.min(1, (t - t0) / 260);
        const e = 1 - (1 - k) ** 3;
        const p = from.map((v, i) => v + ((to[i] ?? v) - v) * e) as Pts;
        el.setAttribute("d", pathFor(p));
        last.current = p;
        anim.current = k < 1 ? requestAnimationFrame(step) : null;
      };
      anim.current = requestAnimationFrame(step);
    };

    // Wait a frame so the new selection has rendered.
    const first = requestAnimationFrame(() => draw(true));
    const onResize = () => requestAnimationFrame(() => draw(false));
    window.addEventListener("resize", onResize);
    const ro = new ResizeObserver(onResize);
    const feed = document.getElementById("cam-feed");
    const holo = document.querySelector(".hologram");
    if (feed) ro.observe(feed);
    if (holo) ro.observe(holo);
    document.fonts?.ready.then(onResize).catch(() => undefined);
    return () => {
      cancelAnimationFrame(first);
      if (anim.current !== null) cancelAnimationFrame(anim.current);
      window.removeEventListener("resize", onResize);
      ro.disconnect();
    };
  }, [selectedId, layoutKey]);

  return (
    <svg className="tether" aria-hidden="true" focusable="false">
      <path ref={pathRef} fill="none" />
    </svg>
  );
}
